import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ComponentImageUploadService } from 'src/app/services/component-image-upload.service';
import { ProductServiceService } from 'src/app/services/product-service.service';
import { TemplateMasterService } from 'src/app/master_modules/masters/apps/template/template-master.service';
import { Observable, startWith, map } from 'rxjs';
import { FormControl } from '@angular/forms';
import { SearchProductComponent } from '../search-product/search-product.component';

@Component({
  selector: 'app-create-category',
  templateUrl: './create-category.component.html',
  styleUrls: ['./create-category.component.scss']
})
export class CreateCategoryComponent {
  constructor(
    public dialogRef: MatDialogRef<CreateCategoryComponent>,
    public ds: DomSanitizer,
    private imageUploadService: ComponentImageUploadService,
    private snackBar: MatSnackBar,
    private productService: ProductServiceService,
    private templateService: TemplateMasterService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.getScreenSize();
  }

  // ---------- VARIABLES ----------
  srcHeight: any;
  srcWidth: any;
  pageLoader: boolean = false;
  submitLoader: boolean = false;
  nodeAttachements: any = [];
  collectionList: any[] = [];
  allCategories: any[] = [];
  selectedCollectionId: string[] = [];
  selectedCollectionData: any[] = [];
  ecomIndustry: any = [];
  ecomMyControl = new FormControl();
  ecomFilteredOptions: Observable<string[]> | any;
  productIds: any;
  categorieData: any = {};

  categoryData: any = {
    subIndustryId: '',
    categoryName: '',
    description: '',
    imgUrl: [],
    itemIds: [],
    collectionIds: []
  };

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?: any) {
    this.srcHeight = window.innerHeight;
    this.srcWidth = window.innerWidth;
  }

  closebutton() {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.getEcommerceIndustry();
    this.getAllCategories(); //  fetch all categories for duplicate validation

    if (this.data.edit === true) {
      this.categoryData.subIndustryId = this.data.data.subIndustryId;
      this.categoryData.categoryName = this.data.data.categoryName;
      this.categoryData.description = this.data.data.description;
      this.categoryData.collectionIds = this.data.data.collectionIds;
      this.nodeAttachements = this.data.data.imgUrl?.map((url) => ({
        url: url,
        attachmentType: 'IMAGE',
        isAlreadyUploaded: true
      }));
      this.selectedCollectionId = this.data?.data?.collectionIds?.map((collection) => collection.refId);
      this.selectedCollectionData = this.data?.data?.collectionIds;
      if (!this.selectedCollectionId) this.selectedCollectionId = [];
      if (!this.selectedCollectionData) this.selectedCollectionData = [];
      this.getCategoryCollectionBySubIndustryId(this.categoryData.subIndustryId);
    }
  }

  // ---------- FETCH ALL CATEGORIES ----------
  getAllCategories() {
    this.productService.getCategoriesList({ pageNo: 0, pageSize: 10000 }).subscribe({
      next: (res: any) => {
        this.allCategories = res?.data?.data || [];
      },
      error: (err: any) => {
        console.error('Error fetching categories:', err);
      }
    });
  }

  // ---------- IMAGE UPLOAD ----------
  updateContactImage(ev: any) {
    this.pageLoader = true;
    for (const fileData of ev.srcElement.files) {
      const reader = new FileReader();
      reader.onload = (response: any) => {
        this.nodeAttachements.push({
          url: response.target.result,
          name: fileData.name,
          attachmentType: fileData.type.includes('image')
            ? 'IMAGE'
            : fileData.type == 'application/pdf'
            ? 'FILE'
            : '',
          file: fileData
        });
      };
      reader.readAsDataURL(fileData);
    }
    this.pageLoader = false;
  }

  delete(index: any) {
    this.nodeAttachements.splice(index, 1);
  }

  // ---------- VALIDATION: CREATE CATEGORY ----------
  async createCategories() {
    this.submitLoader = true;

    const newName = (this.categoryData.categoryName || '').trim();

    //  Empty name check
    if (!newName) {
      this.submitLoader = false;
      this.snackBar.open('Category Name cannot be empty', 'Close', { duration: 2000 });
      return;
    }

    //  Invalid characters check
    const invalidCharRegex = /[^a-zA-Z0-9\s]/;
    if (invalidCharRegex.test(newName)) {
      this.submitLoader = false;
      this.snackBar.open('Category Name contains invalid characters!', 'Close', { duration: 2000 });
      return;
    }

    //  Max length check
    if (newName.length > 50) {
      this.submitLoader = false;
      this.snackBar.open('Category Name cannot exceed 50 characters', 'Close', { duration: 2000 });
      return;
    }

    //  Duplicate check (case-insensitive)
    const duplicate = this.allCategories.some(
      (cat: any) =>
        (cat.categoryName || '').trim().toLowerCase() === newName.toLowerCase() &&
        (!this.data.edit || cat.categoryId !== this.data?.data?.categoryId)
    );

    if (duplicate) {
      this.submitLoader = false;
      this.snackBar.open(' Category with this name already exists!', 'Close', { duration: 2000 });
      return;
    }

    // ---------- IMAGE UPLOAD ----------
    let imageUrls: string[] = [];
    for (let item of this.nodeAttachements) {
      if (!item.file) {
        imageUrls.push(item.url);
        continue;
      }
      let imageResponse: any = await this.imageUploadService.uploadComponentsImages(item.file, 'category_images');
      imageUrls.push(imageResponse['Location']);
    }

    this.categoryData.imgUrl = imageUrls;
    this.categoryData.itemIds = this.productIds;
    this.categoryData.collectionIds = this.selectedCollectionData;
    if (this.data.edit) {
      this.categoryData.categoryId = this.data.data.categoryId;
    }

    // ---------- SAVE CATEGORY ----------
    this.productService.addNewCategory(this.categoryData).subscribe({
      next: () => {
        this.submitLoader = false;
        this.dialogRef.close();
        this.snackBar.open(
          this.data.edit
            ? ' Category Updated Successfully'
            : ' Category Created Successfully',
          'Close',
          { duration: 2000 }
        );
      },
      error: (err: any) => {
        this.submitLoader = false;
        console.error('Error:', err);
        this.snackBar.open(' Failed to save category', 'Close', { duration: 2000 });
      }
    });
  }

  // ---------- INDUSTRY ----------
  getEcommerceIndustry() {
    this.templateService.getAllEcommerceIndustries().subscribe((res: any) => {
      res.data.forEach((data: any) => {
        this.ecomIndustry.push({
          name: data.name,
          industryId: data.parent.id,
          subIndustryId: data.id,
          industryName: data.parent.name,
          subIndustryName: data.name
        });
      });

      if (this.ecomIndustry.length > 0) {
        const defaultOption = this.ecomIndustry[0];
        this.ecomMyControl.setValue(defaultOption.name);
        this.selectBusinessType(defaultOption);
      }

      this.ecomFilteredOptions = this.ecomMyControl.valueChanges.pipe(
        startWith(''),
        map((value) => this._ecomFilter(value))
      );
    });
  }

  selectBusinessType(ev: any) {
    this.categorieData.subIndustryId = ev.subIndustryId;
    this.categorieData.subIndustryName = ev.name;
    this.ecomMyControl.setValue(ev.name);
  }

  onSubIndustryChange(subIndustryName: any) {
    const selectedOption = this.ecomIndustry.find((option) => option.name === subIndustryName);
    if (selectedOption) {
      this.selectBusinessType(selectedOption);
    }
  }

  private _ecomFilter(value: any) {
    const filterValue = value.toLowerCase();
    return this.ecomIndustry.filter((option: any) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  // ---------- COLLECTION ----------
  getCategoryCollectionBySubIndustryId(subId: any) {
    this.productService.getCollectionBySubIndustryId(subId).subscribe((res: any) => {
      this.collectionList = res.data;
    });
  }

  onSelectAllCollection(checkboxElement) {
    if (checkboxElement.checked) {
      this.selectedCollectionId = this.collectionList.map((element) => element.collectionId);
      this.selectedCollectionData = this.collectionList.map((element) => ({
        refId: element.collectionId,
        refName: element.CollectionName
      }));
    } else {
      this.selectedCollectionData = [];
      this.selectedCollectionId = [];
    }
  }

  selectCollection(checkBoxElement, collection: any, allCollectionCheckbox) {
    if (checkBoxElement.selected) {
      this.selectedCollectionId.push(collection.collectionId);
      this.selectedCollectionData.push({ refId: collection.collectionId, refName: collection.CollectionName });
    } else {
      this.selectedCollectionId = this.selectedCollectionId.filter((id: any) => id != collection.collectionId);
      this.selectedCollectionData = this.selectedCollectionData.filter((data: any) => data.refId != collection.collectionId);
      allCollectionCheckbox = this.selectedCollectionData.length === this.collectionList.length;
    }
  }

  openDialog() {
    let searchDialog = this.templateService.openDialog(SearchProductComponent, '69vh', '40%', '');
    searchDialog.afterClosed().subscribe((res: any) => {
      this.productIds = res.productIds;
    });
  }
}
